import React, {useState, useEffect, useContext} from "react";
import {motion, useMotionValue, animate} from "framer-motion";
import {
    Chart as ChartJS,
    LineElement,
    LinearScale,
    PointElement,
    CategoryScale,
    Tooltip,
    Legend,
} from "chart.js";
import ZoomPlugin from "chartjs-plugin-zoom";

import Url from "../../../url/Url";
import {IoClose} from "react-icons/io5";
import {IoMdNotifications} from "react-icons/io";
import ImageWithLoader from "../../../components/imageLoader/ImageWithLoader";
import AnimatedPrice from "./AnimatedPrice";
import PriceChart from "./PriceChart";
import {SafeAreaContext} from "../../../App";
import Currency from "../../../services/Currency";
import {getSocket} from "../../../services/socket";
import externalLinkLogo from "../../../assets/images/external-link-svgrepo-com.svg";


// Register Chart.js components + Zoom plugin (do this once)
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Tooltip,
    Legend,
    ZoomPlugin,
);

const TIMEFRAMES = [
    {label: "1D", value: "1D"},
    {label: "1W", value: "7D"},
    {label: "1M", value: "1M"},
    {label: "1Y", value: "1Y"},
    {label: "ALL", value: "ALL"},
];

const Chart = ({openChart, symbol, onClose}) => {
    const {setSafeAreaColor} = useContext(SafeAreaContext);

    const [labels, setLabels] = useState([]);
    const [dataPoints, setDataPoints] = useState([]);
    const [timeframe, setTimeframe] = useState("1D");
    const [priceColor, setPriceColor] = useState("text-primary");
    const [chartLoading, setChartLoading] = useState(false);
    const [chartError, setChartError] = useState(false);
    const [displayedPrice, setDisplayedPrice] = useState(null);
    const [minPrice, setMinPrice] = useState(null);
    const [maxPrice, setMaxPrice] = useState(null);
    const y = useMotionValue(0);

    const livePrice =
        displayedPrice ?? symbol?.price ?? symbol?.last ?? symbol?.volume ?? 0;

    // Safe area
    useEffect(() => {
        setSafeAreaColor({background: "rgba(0, 0, 0, 0.5)"});
    }, [setSafeAreaColor]);

    // Live price via WebSocket
    useEffect(() => {
        if (!openChart || !symbol?.symbol) return;

        const socket = getSocket();
        const handleLiveUpdate = (data) => {
            if (data.symbol === symbol.symbol) {
                const newPrice = data.price ?? data.last ?? data.value;
                if (newPrice != null) {
                    setDisplayedPrice(newPrice);

                    // Update last point of chart in real-time
                    setDataPoints((prev) => {
                        if (prev.length === 0) return prev;
                        const updated = [...prev];
                        updated[updated.length - 1] = Number(newPrice);
                        return updated;
                    });
                }
            }
        };

        socket.on("currency:liveUpdate", handleLiveUpdate);

        return () => socket.off("currency:liveUpdate", handleLiveUpdate);
    }, [openChart, symbol?.symbol]);

    // Fetch historical chart
    useEffect(() => {
        if (!openChart || !symbol?.symbol) return;

        let cancelled = false;

        const fetchChartData = async () => {
            setChartLoading(true);
            setChartError(false);
            try {
                const res = await Currency.getChartData({
                    symbol: symbol.symbol,
                    period: timeframe,
                });


                if (cancelled) return;

                const payload = res?.data?.en_message ?? res?.data ?? [];
                console.log("chart payload sample:", payload[0]);
                let newLabels = [];
                let newPoints = [];


                if (Array.isArray(payload)) {
                   const records = payload
    .map((item) => ({
        label: item.createdAt,
        price: Number(item.price),
    }))
    .filter(
        (item) =>
            !Number.isNaN(new Date(item.label).getTime()) &&
            Number.isFinite(item.price)
    );

                    const YEAR_GROUPED = ["ALL"];

                    if (timeframe === "1Y" || timeframe === "ALL") {
                        const groups = new Map();

                        records.forEach(({label, price}) => {
                            const date = new Date(label);
                            if (isNaN(date.getTime()) || !Number.isFinite(price)) return;

                            const year = date.getFullYear();
                            const month = String(date.getMonth() + 1).padStart(2, "0");

                            const byYear = YEAR_GROUPED.includes(timeframe);
                            const groupKey = byYear ? String(year) : `${year}-${month}`;

                            if (!groups.has(groupKey)) {
                                groups.set(groupKey, {
                                    total: 0,
                                    count: 0,
                                    label: byYear
                                        ? new Date(year, 5, 15, 12).toISOString()
                                        : date.toISOString(),
                                });
                            }

                            const group = groups.get(groupKey);
                            group.total += price;
                            group.count += 1;
                        });

                        const groupedRecords = [...groups.entries()]
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([, group]) => ({
                                label: group.label,
                                price: group.total / group.count,   // yearly average
                            }));

                        newLabels = groupedRecords.map((p) => p.label);
                        newPoints = groupedRecords.map((p) => p.price);
                    } else {
                        newLabels = records.map((p) => p.label);
                        newPoints = records.map((p) => p.price);
                    }

                    if (newPoints.length > 0) {
                        setMinPrice(Math.min(...newPoints));
                        setMaxPrice(Math.max(...newPoints));
                    } else {
                        setMinPrice(null);
                        setMaxPrice(null);
                    }
                } else if (payload?.labels && payload?.prices) {
                    newLabels = payload.labels;
                    newPoints = payload.prices;
                }

                setLabels(newLabels);
                setDataPoints(newPoints);
            } catch (err) {
                console.error("[chart data error]", err);
                if (!cancelled) setChartError(true);
            } finally {
                if (!cancelled) setChartLoading(false);
            }
        };

        fetchChartData();

        // return () => {
        //     cancelled = true;
        // };
    }, [openChart, symbol?.symbol, timeframe]);

    // Drag handlers
    const handleDragEnd = () => {
        if (y.get() > 150) onClose();
        else animate(y, 0, {duration: 0.2});
    };

    useEffect(() => {
        if (openChart) y.set(0);
    }, [openChart, y]);

    if (!openChart) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
            <motion.div
                drag="y"
                dragElastic={0.2}
                dragConstraints={{top: 0}}
                style={{y}}
                onDragEnd={handleDragEnd}
                onClick={(e) => e.stopPropagation()}
                className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl h-[75vh] overflow-hidden flex flex-col shadow-2xl"
            >
                {/* Header */}
                <div className="p-6 border-b border-border">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-4">
                            <div className="w-16 h-16 rounded-3xl overflow-hidden shadow-lg">
                                <ImageWithLoader
                                    src={`${Url}${symbol?.image?.path}`}
                                    alt={symbol?.symbol}
                                />
                            </div>
                            <div>
                                <h3 className="font-bold text-2xl text-text-primary mb-1">
                                    {symbol?.symbol}
                                </h3>
                                <AnimatedPrice value={livePrice} priceColor={priceColor}/>
                                <div className="flex items-center space-x-4 text-xs">
                                    <div>
                                        Max:{" "}
                                        <span className="text-green-600 font-bold">
                      ${maxPrice}
                    </span>
                                    </div>
                                    <div>
                                        Min:{" "}
                                        <span className="text-red-600 font-bold">
                      ${minPrice}
                    </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col items-center gap-3">
                            <div className="flex items-center space-x-3">
                                {/* <button
                                    className="w-12 h-12 bg-orange-50 hover:bg-orange-100 rounded-2xl flex items-center justify-center">
                                    <IoMdNotifications className="text-orange-500 text-xl"/>
                                </button> */}
                                <button
                                    onClick={onClose}
                                    className="w-14 h-14 bg-gray-50 hover:bg-gray-100 rounded-3xl flex items-center justify-center"
                                >
                                    <IoClose className="text-2xl text-text-secondary"/>
                                </button>
                            </div>
                            <a
                                href={`https://www.tradingview.com/symbols/${symbol?.symbol}/`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-14 h-14 bg-gray-50 hover:bg-gray-100 p-3 rounded-3xl"
                                aria-label={`View ${symbol?.symbol} on TradingView`}
                            >
                                <img src={externalLinkLogo} alt={"View on TradingView"}/>
                            </a>
                        </div>


                    </div>
                </div>

                {/* Chart Area */}
                <div className="flex-1 flex flex-col px-0 pb-6">
                    <div className="relative flex-1 bg-white">
                        {chartLoading && (
                            <div className="absolute inset-0 flex items-center justify-center text-text-secondary">
                                Loading chart...
                            </div>
                        )}
                        {chartError && !chartLoading && (
                            <div className="absolute inset-0 flex items-center justify-center text-red-500">
                                Couldn't load chart data
                            </div>
                        )}

                        {!chartLoading && (
                            <PriceChart
                                labels={labels}
                                data={dataPoints}
                                symbol={symbol?.symbol}
                                currentTimeframe={timeframe}
                                currentPrice={livePrice}
                                setDisplayedPrice={setDisplayedPrice}
                                setPriceColor={setPriceColor}
                                openChart={openChart}
                                displayedPrice={displayedPrice}
                            />
                        )}
                    </div>

                    {/* Timeframe Buttons */}
                    <div className="px-6 pt-4">
                        <div className="grid grid-cols-5 gap-2 w-full">
                            {TIMEFRAMES.map((tf) => (
                                <button
                                    key={tf.value}
                                    onClick={() => setTimeframe(tf.value)}
                                    className={`px-3 py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 ${
                                        timeframe === tf.value
                                            ? "bg-gradient-to-r from-primary to-primary-light text-white shadow-lg"
                                            : "bg-gray-100 hover:bg-gray-200 text-text-secondary"
                                    }`}
                                >
                                    {tf.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default Chart;
